import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Download, Loader2, Pencil, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { CourseImportDialog } from "@/components/CourseImportDialog";
import { toast } from "sonner";
import { listCourses, saveCourse, deleteCourse, setCourseTrainers } from "@/lib/courses.functions";
import { listTrainers } from "@/lib/trainers.functions";
import { getTpCourseLearningEvents } from "@/lib/training-pipeline.functions";
import { getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { cadenceLabel, formatTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/courses")({
  head: () => ({
    meta: [
      { title: "Courses | Leadership Edge Scheduling" },
      { name: "description", content: "Define course duration, cadence, and which trainers are authorized to teach." },
      { property: "og:title", content: "Courses | Leadership Edge Scheduling" },
      { property: "og:description", content: "Course duration, cadence, and trainer authorizations." },
    ],
  }),
  component: CoursesPage,
});

/** One Virtual Class Learning Event as the form holds it. */
interface VirtualClassRow {
  lms_class_id: number;
  name: string;
}

interface CourseForm {
  id?: string;
  name: string;
  code: string;
  course_id: string;
  description: string;
  duration_weeks: number;
  cadence_gap_weeks: number;
  last_virtual_session_date: string;
  last_virtual_session_time: string;
  is_active: boolean;
  /** Virtual Classes shown in the dialog; saved with the course. */
  virtual_classes: VirtualClassRow[];
  /** True once the user synced in this dialog session (list is then written). */
  virtual_classes_synced: boolean;
}

const blank: CourseForm = {
  name: "",
  code: "",
  course_id: "",
  description: "",
  duration_weeks: 5,
  cadence_gap_weeks: 1,
  last_virtual_session_date: "",
  last_virtual_session_time: "",
  is_active: true,
  virtual_classes: [],
  virtual_classes_synced: false,
};



function CoursesPage() {
  const queryClient = useQueryClient();
  const fetchMe = useServerFn(getMe);
  const fetchCourses = useServerFn(listCourses);
  const save = useServerFn(saveCourse);
  const remove = useServerFn(deleteCourse);
  const setTrainers = useServerFn(setCourseTrainers);
  const fetchTpLearningEvents = useServerFn(getTpCourseLearningEvents);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({ queryKey: ["courses"], queryFn: () => fetchCourses() });
  const fetchTrainers = useServerFn(listTrainers);
  const [exporting, setExporting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const [search, setSearch] = useState("");
  const [form, setForm] = useState<CourseForm | null>(null);

  const [authFor, setAuthFor] = useState<string | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["courses"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const saveMutation = useMutation({
    mutationFn: (payload: CourseForm) =>
      save({
        data: {
          ...(payload.id ? { id: payload.id } : {}),
          name: payload.name,
          code: payload.code.trim() ? payload.code.trim() : null,
          course_id: payload.course_id.trim() ? Number(payload.course_id.trim()) : null,
          description: payload.description.trim() ? payload.description.trim() : null,
          duration_weeks: payload.duration_weeks,
          cadence_gap_weeks: payload.cadence_gap_weeks,
          last_virtual_session_date: payload.last_virtual_session_date.trim()
            ? payload.last_virtual_session_date.trim()
            : null,
          last_virtual_session_time: payload.last_virtual_session_time.trim()
            ? payload.last_virtual_session_time.trim()
            : null,

          is_active: payload.is_active,
          ...(payload.virtual_classes_synced ? { virtual_classes: payload.virtual_classes } : {}),
        },
      }),

    onSuccess: () => {
      toast.success("Course saved.");
      setForm(null);
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  // Pulls this course's Virtual Class Learning Events from Training Pipeline.
  // Server-side only: the browser never sees TP credentials.
  const syncVirtualClasses = useMutation({
    mutationFn: async (tpCourseId: number) => {
      const result = await fetchTpLearningEvents({ data: { courseId: tpCourseId } });
      return result;
    },
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      const rows: VirtualClassRow[] = (result.virtualClassSummaries ?? [])
        .filter((entry) => Number.isInteger(entry.class_id) && entry.name.trim().length > 0)
        .map((entry) => ({ lms_class_id: entry.class_id, name: entry.name }));
      setForm((current) =>
        current ? { ...current, virtual_classes: rows, virtual_classes_synced: true } : current,
      );
      if (rows.length === 0) {
        toast.message("No Virtual Class Learning Events found for this TP Course.");
      } else {
        toast.success(`${rows.length} Virtual Class${rows.length === 1 ? "" : "es"} loaded. Review, then save.`);
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Course removed.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const authMutation = useMutation({
    mutationFn: (payload: { courseId: string; trainerIds: string[] }) => setTrainers({ data: payload }),
    onSuccess: () => {
      toast.success("Authorizations updated.");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const courses = data?.courses ?? [];
  const trainers = data?.trainers ?? [];
  const links = data?.links ?? [];
  const isAdmin = me?.isAdmin ?? false;

  async function exportToExcel() {
    if (exporting) return;
    setExporting(true);
    try {
      const [XLSX, trainerData] = await Promise.all([import("xlsx"), fetchTrainers()]);
      const exceptTrainers = new Map(
        (trainerData.trainers ?? [])
          .filter((t) => t.authorization_mode === "except")
          .map((t) => [t.id, t.full_name ?? t.email ?? "Unnamed"]),
      );
      const rows = courses.map((c) => {
        const excluded = (trainerData.exclusions ?? [])
          .filter((e) => e.course_id === c.id && exceptTrainers.has(e.trainer_id))
          .map((e) => exceptTrainers.get(e.trainer_id)!)
          .sort((a, b) => a.localeCompare(b));
        return {
          Name: c.name,
          Code: c.code ?? "",
          "Training Pipeline course number": c.course_id ?? "",
          "Length (weeks)": c.duration_weeks,
          "Cadence gap (weeks)": c.cadence_gap_weeks,
          "Date of the last virtual session": c.last_virtual_session_date ?? "",
          "Time of the last virtual session": c.last_virtual_session_time
            ? (() => {
                // "9:00 AM" style so the Import button can read it back.
                const [h, m] = c.last_virtual_session_time.split(":").map(Number);
                const hr = (h ?? 0) % 12 === 0 ? 12 : (h ?? 0) % 12;
                return `${hr}:${String(m ?? 0).padStart(2, "0")} ${(h ?? 0) >= 12 ? "PM" : "AM"}`;
              })()
            : "",
          "Active (Yes/No)": c.is_active ? "Yes" : "No",
          Notes: c.description ?? "",
          "Authorized trainers": excluded.length ? `All except: ${excluded.join(", ")}` : "All",
        };
      });
      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Courses");
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      XLSX.writeFile(book, `courses-export-${today}.xlsx`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const authorizedIds = (courseId: string) => links.filter((l) => l.course_id === courseId).map((l) => l.trainer_id);

  // Virtual Classes already stored for one course, in their saved order.
  const storedVirtualClasses = data?.virtualClasses ?? [];
  const virtualClassesFor = (courseId: string): VirtualClassRow[] =>
    storedVirtualClasses
      .filter((v) => v.course_id === courseId)
      .map((v) => ({ lms_class_id: v.lms_class_id, name: v.name }));

  const query = search.trim().toLowerCase();
  const visibleCourses = query
    ? courses.filter(
        (c) =>
          c.name.toLowerCase().includes(query) || (c.code ?? "").toLowerCase().includes(query),
      )
    : courses;

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Courses"
        description="Each course meets once per week for its set number of weeks. Cadence controls the gap before the next run; authorized trainers limit who can be assigned."
        actions={
          <div className="flex flex-wrap gap-2">
            {isAdmin ? (
              <Button variant="outline" onClick={exportToExcel} disabled={exporting || courses.length === 0}>
                {exporting ? <Loader2 className="animate-spin" /> : <Download />} Export to Excel
              </Button>
            ) : null}
            {isAdmin ? (
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                title="Use the Export file as your starting template"
              >
                <Upload /> Import from Excel
              </Button>
            ) : null}
            {isAdmin ? (
              <Button variant="brass" onClick={() => setForm({ ...blank })}>
                <Plus /> New course
              </Button>
            ) : null}
          </div>
        }
      />

      {isAdmin ? (
        <CourseImportDialog
          courses={courses}
          open={importOpen}
          onOpenChange={setImportOpen}
          onImported={invalidate}
        />
      ) : null}

      <div className="mb-4 max-w-sm">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by course name or code…"
          aria-label="Search courses"
        />
      </div>

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : courses.length === 0 ? (
        <EmptyState title="No courses yet" body="Add a course with its duration and cadence to start building schedules." />
      ) : visibleCourses.length === 0 ? (
        <EmptyState title="No matching courses" body="No course matches that name or code. Try a different search." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {visibleCourses.map((course) => {

            const authorized = authorizedIds(course.id);
            return (
              <article key={course.id} className="panel p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-xl text-foreground">{course.name}</h2>
                    <p className="mt-1 font-mono text-xs text-muted-foreground">
                      {course.code ? `${course.code} · ` : ""}
                      {course.duration_weeks} week{course.duration_weeks === 1 ? "" : "s"} · once per week
                    </p>

                  </div>
                  {!course.is_active ? (
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                      Inactive
                    </span>
                  ) : null}
                </div>

                {course.description ? (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{course.description}</p>
                ) : null}

                <dl className="mt-4 grid grid-cols-2 gap-4 border-t border-border pt-4 text-sm">
                  <div>
                    <dt className="eyebrow">Cadence</dt>
                    <dd className="mt-1 text-foreground">
                      {cadenceLabel(course.cadence_gap_weeks ?? 1)}
                    </dd>
                  </div>
                  <div>
                    <dt className="eyebrow">Authorized</dt>
                    <dd className="mt-1 text-foreground">
                      {authorized.length} trainer{authorized.length === 1 ? "" : "s"}
                    </dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="eyebrow">Last virtual session</dt>
                    <dd className="mt-1 text-foreground">
                      {course.last_virtual_session_date
                        ? `${new Date(`${course.last_virtual_session_date}T12:00:00Z`).toLocaleDateString("en-US", {
                            month: "long",
                            day: "numeric",
                            year: "numeric",
                            timeZone: "UTC",
                          })}${
                            course.last_virtual_session_time
                              ? ` at ${formatTime(course.last_virtual_session_time.slice(0, 5))}`
                              : ""
                          }`
                        : "Not taught yet"}
                    </dd>
                  </div>
                </dl>


                {isAdmin ? (
                  <div className="mt-5 flex flex-wrap gap-2">
                    <Button
                      variant="soft"
                      size="sm"
                      onClick={() =>
                        setForm({
                          id: course.id,
                          name: course.name,
                          code: course.code ?? "",
                          course_id: course.course_id === null ? "" : String(course.course_id),
                          description: course.description ?? "",
                          duration_weeks: course.duration_weeks ?? 5,

                          cadence_gap_weeks: course.cadence_gap_weeks ?? 1,
                          last_virtual_session_date: course.last_virtual_session_date ?? "",
                          last_virtual_session_time: (course.last_virtual_session_time ?? "").slice(0, 5),
                          is_active: course.is_active,
                          virtual_classes: virtualClassesFor(course.id),
                          virtual_classes_synced: false,
                        })
                      }

                    >
                      <Pencil /> Edit
                    </Button>
                    <Button variant="soft" size="sm" onClick={() => setAuthFor(course.id)}>
                      Authorized trainers
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => {
                        if (confirm(`Remove ${course.name}? Its sessions will be removed too.`)) {
                          deleteMutation.mutate(course.id);
                        }
                      }}
                    >
                      <Trash2 /> Remove
                    </Button>
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{form?.id ? "Edit course" : "New course"}</DialogTitle>
            <DialogDescription>Cadence controls how often the builder schedules this course.</DialogDescription>
          </DialogHeader>

          {form ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
                <div className="space-y-1.5">
                  <Label htmlFor="course-name">Name</Label>
                  <Input
                    id="course-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Coaching Essentials"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="course-code">Code</Label>
                  <Input
                    id="course-code"
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    placeholder="CE-100"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="course-tp-id">
                  Training Pipeline Course Id <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="course-tp-id"
                  type="number"
                  min={1}
                  step={1}
                  required
                  value={form.course_id}
                  onChange={(e) => setForm({ ...form, course_id: e.target.value })}
                  placeholder="139"
                />
                <p className="text-xs text-muted-foreground">
                  Required. Numbers only. This is the course number Training Pipeline uses, kept separate from the code above.
                </p>
              </div>

              <div className="space-y-2 rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="text-sm">Virtual Class Learning Events</Label>
                  <Button
                    variant="soft"
                    size="sm"
                    disabled={syncVirtualClasses.isPending || !form.course_id.trim()}
                    onClick={() => {
                      const tpId = Number(form.course_id.trim());
                      if (!Number.isInteger(tpId) || tpId <= 0) {
                        toast.error("Enter the Training Pipeline Course Id first.");
                        return;
                      }
                      syncVirtualClasses.mutate(tpId);
                    }}
                  >
                    {syncVirtualClasses.isPending ? (
                      <>
                        <Loader2 className="animate-spin" /> Syncing…
                      </>
                    ) : (
                      <>
                        <RefreshCw /> Sync Virtual Classes with TP
                      </>
                    )}
                  </Button>
                </div>

                {form.virtual_classes.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {form.virtual_classes_synced
                      ? "No Virtual Class Learning Events found for this TP Course."
                      : "Enter the Training Pipeline Course Id above, then sync to load this course's virtual classes."}
                  </p>
                ) : (
                  <div className="overflow-hidden rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-surface text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="px-3 py-2 font-medium">LMS Class ID</th>
                          <th className="px-3 py-2 font-medium">Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {form.virtual_classes.map((entry) => (
                          <tr key={entry.lms_class_id} className="border-t border-border">
                            <td className="px-3 py-2 font-mono text-xs">{entry.lms_class_id}</td>
                            <td className="px-3 py-2">{entry.name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Syncing only loads the list. It is stored with this course when you save.
                </p>
              </div>




              <div className="space-y-1.5">
                <Label htmlFor="course-desc">Notes (optional)</Label>
                <Textarea
                  id="course-desc"
                  rows={3}
                  placeholder="Anything the scheduler should know about this course."
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="course-weeks">Length (weeks)</Label>
                  <Input
                    id="course-weeks"
                    type="number"
                    min={1}
                    max={12}
                    value={form.duration_weeks}
                    onChange={(e) => setForm({ ...form, duration_weeks: Number(e.target.value) })}
                  />
                  <p className="text-xs text-muted-foreground">
                    One session per week, same day and time for all {Math.max(1, form.duration_weeks)} weeks.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="course-gap">Cadence (weeks skipped after it ends)</Label>
                  <Input
                    id="course-gap"
                    type="number"
                    min={0}
                    max={52}
                    value={form.cadence_gap_weeks}
                    onChange={(e) => setForm({ ...form, cadence_gap_weeks: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="course-last-session">Date of the last virtual session</Label>
                  <Input
                    id="course-last-session"
                    type="date"
                    value={form.last_virtual_session_date}
                    onChange={(e) => setForm({ ...form, last_virtual_session_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="course-last-session-time">Time of the last virtual session</Label>
                  <Input
                    id="course-last-session-time"
                    type="time"
                    step={1800}
                    value={form.last_virtual_session_time}
                    onChange={(e) => setForm({ ...form, last_virtual_session_time: e.target.value })}
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank if the course has not run yet. The cadence gap is counted from this date, and the app works
                back one week per session to keep the earlier run of this course out of the way.
              </p>




              <p className="rounded-md bg-surface px-3 py-2 text-sm text-muted-foreground">
                {cadenceLabel(form.cadence_gap_weeks)}
              </p>

              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <Label htmlFor="course-active" className="cursor-pointer">
                  Active
                </Label>
                <Switch
                  id="course-active"
                  checked={form.is_active}
                  onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setForm(null)}>
              Cancel
            </Button>
            <Button
              variant="brass"
              disabled={saveMutation.isPending || !form?.name.trim() || !form?.course_id.trim()}
              onClick={() => {
                if (!form) return;
                const tpId = Number(form.course_id.trim());
                if (!Number.isInteger(tpId) || tpId <= 0) {
                  toast.error("Training Pipeline Course Id is required.");
                  return;
                }
                saveMutation.mutate(form);
              }}
            >
              Save course
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={authFor !== null} onOpenChange={(open) => !open && setAuthFor(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Authorized to teach</DialogTitle>
            <DialogDescription>Only these trainers can be assigned to this course.</DialogDescription>
          </DialogHeader>

          {authFor ? (
            trainers.length === 0 ? (
              <p className="text-sm text-muted-foreground">Add trainers first.</p>
            ) : (
              <ul className="space-y-2">
                {(() => {
                  const current = authorizedIds(authFor);
                  const allChecked = trainers.every((trainer) => current.includes(trainer.id));
                  return (
                    <li className="flex items-center gap-3 rounded-md border border-border bg-muted/40 px-3 py-2.5">
                      <Checkbox
                        id="auth-select-all"
                        checked={allChecked}
                        onCheckedChange={(value) => {
                          authMutation.mutate({
                            courseId: authFor,
                            trainerIds: value === true ? trainers.map((trainer) => trainer.id) : [],
                          });
                        }}
                      />
                      <Label htmlFor="auth-select-all" className="cursor-pointer text-sm font-medium">
                        Select all trainers
                      </Label>
                    </li>
                  );
                })()}

                {trainers.map((trainer) => {
                  const current = authorizedIds(authFor);
                  const checked = current.includes(trainer.id);
                  return (
                    <li key={trainer.id} className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5">
                      <Checkbox
                        id={`auth-${trainer.id}`}
                        checked={checked}
                        onCheckedChange={(value) => {
                          const next = value === true ? [...current, trainer.id] : current.filter((id) => id !== trainer.id);
                          authMutation.mutate({ courseId: authFor, trainerIds: next });
                        }}
                      />
                      <Label htmlFor={`auth-${trainer.id}`} className="cursor-pointer font-normal">
                        {trainer.full_name}
                        <span className="ml-2 text-xs text-muted-foreground">{trainer.email}</span>
                      </Label>
                    </li>
                  );
                })}
              </ul>
            )
          ) : null}

          <DialogFooter>
            <Button variant="soft" onClick={() => setAuthFor(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
